import React, { useState } from "react";
import { Share2, Copy, ExternalLink, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Modal, { ModalHeader } from "./Modal";

/**
 * Portaled Share Link modal. Shows the current share URL with Copy / Open /
 * Revoke actions, plus the "Show top risks" toggle. Opens on button click so
 * users can re-copy the URL any time (previously it was only toast-based).
 */
export default function ShareLinkButton({
  project, locked, onShare, onRevoke, onToggleRisks, requireActive,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const enabled = !!project?.share_enabled;
  const token = project?.share_token;
  const shareUrl = token ? `${window.location.origin}/share/${token}` : "";
  const close = () => setOpen(false);

  const openModal = async () => {
    if (!enabled) {
      setBusy(true);
      try { await (requireActive ? requireActive(onShare)() : onShare()); } finally { setBusy(false); }
    }
    setOpen(true);
  };

  const copy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied");
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await onRevoke();
      close();
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={openModal}
        disabled={locked || busy}
        data-testid="share-btn"
        className={`inline-flex items-center gap-2 border ${enabled?"border-emerald-600/50 bg-emerald-500/10 text-emerald-300":"border-slate-700 text-slate-200"} ${locked?"opacity-50":"hover:border-blue-500"} px-3 py-2 rounded-lg text-sm`}
      >
        {busy ? <Loader2 className="animate-spin" size={14}/> : <Share2 size={14}/>}
        {enabled ? "Share on — manage" : "Share link"}
      </button>
      <Modal open={open} onClose={close} testId="share-link-modal">
        <ModalHeader icon={Share2} title="Public share link" onClose={close} testId="share-close-btn"/>
        <p className="text-xs text-slate-400 mb-3">
          Anyone with this link can view a read-only project portal — no signup required. Revoke it any time to invalidate the URL immediately.
        </p>
        {shareUrl ? (
          <div className="flex items-center gap-2 mb-4">
            <input
              readOnly
              value={shareUrl}
              data-testid="share-url-input"
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2 outline-none focus:border-blue-500 font-mono"
            />
            <button onClick={copy} data-testid="share-copy-btn"
              className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-2 rounded-lg">
              <Copy size={14}/> Copy
            </button>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="share-open-btn"
               className="inline-flex items-center gap-1 border border-slate-700 hover:border-blue-500 text-slate-200 text-sm px-3 py-2 rounded-lg">
              <ExternalLink size={14}/> Open
            </a>
          </div>
        ) : (
          <div className="text-sm text-slate-400 mb-4">Generating link…</div>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-200 mb-5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!project?.share_show_risks}
            onChange={(e) => onToggleRisks(e.target.checked)}
            data-testid="toggle-share-risks"
            className="w-4 h-4 accent-blue-500"
          />
          <span>
            Show top risks on the portal
            <span className="block text-[11px] text-slate-500 leading-tight">Sanitized titles only — never includes mitigation notes or internal context.</span>
          </span>
        </label>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
          <button onClick={close} className="text-sm text-slate-400 hover:text-white px-3 py-2">Close</button>
          <button onClick={revoke} disabled={busy || !enabled} data-testid="share-revoke-btn"
            className="inline-flex items-center gap-1 border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-sm px-3 py-2 rounded-lg disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Trash2 size={14}/>} Revoke link
          </button>
        </div>
      </Modal>
    </>
  );
}
